import { useEffect, useState } from 'react'
import { useNotificationContext } from '../../contexts/NotificationContext'
import {
  Realization, TimelineEvent, authHeaders, stageMeta,
} from './realizationConstants'
import { MediaBadges, StageBadge, ConditionsExpiryBadge, ConditionsResult } from './RealizationBadges'

interface Props {
  token: string
  realizationId: number
  canManage: boolean
  onClose: () => void
  onChanged: () => void
  onEdit: (r: Realization) => void
}

const eventLabel = (t: string): string => {
  if (t.startsWith('stage:')) return `➡️ Etap: ${stageMeta(t.slice(6)).label}`
  const map: Record<string, string> = {
    application: '📥 Wniosek zarejestrowany',
    conditions: '📝 Decyzja o warunkach',
    agreement: '🤝 Uzgodnienie projektu',
    acceptance: '🏁 Odbiór',
    contract: '📄 Umowa zawarta',
  }
  return map[t] || `• ${t}`
}

export default function RealizationDetailModal({ token, realizationId, canManage, onClose, onChanged, onEdit }: Props) {
  const { showSuccess, showError } = useNotificationContext()
  const [r, setR] = useState<Realization | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [dialog, setDialog] = useState<null | 'conditions' | 'agreement' | 'acceptance' | 'contract' | 'stage'>(null)

  const load = async () => {
    setLoading(true)
    try {
      const res = await fetch(`/api/realizations/${realizationId}`, { headers: authHeaders(token) })
      setR(res.ok ? await res.json() : null)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { load() }, [realizationId]) // eslint-disable-line react-hooks/exhaustive-deps

  const post = async (path: string, body: any, method: 'POST' | 'PUT' = 'POST') => {
    setBusy(true)
    try {
      const res = await fetch(`/api/realizations/${realizationId}${path}`, {
        method,
        headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (res.ok) {
        showSuccess('Zapisano')
        setDialog(null)
        await load()
        onChanged()
        return true
      }
      const err = await res.json().catch(() => ({}))
      showError('Błąd', err.detail || 'Nie udało się zapisać')
      return false
    } catch {
      showError('Błąd', 'Błąd połączenia')
      return false
    } finally {
      setBusy(false)
    }
  }

  const setStage = (stage: string, notes = '') => post('/stage', { stage, notes }, 'PUT')

  if (!r && !loading) {
    return (
      <Overlay onClose={onClose}>
        <div className="p-6 text-center text-gray-500">Nie znaleziono realizacji.</div>
      </Overlay>
    )
  }

  return (
    <Overlay onClose={onClose}>
      {loading || !r ? (
        <div className="p-10 text-center text-gray-500">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3" />Ładowanie…
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="px-5 sm:px-6 py-4 border-b border-gray-200 shrink-0">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 truncate">{r.applicant_name}</h2>
                <p className="text-sm text-gray-500 truncate">{r.location} • dz. {r.plot_number}</p>
              </div>
              <button onClick={onClose} className="p-1 -mr-1 text-gray-400 hover:text-gray-600 text-xl font-bold shrink-0">✕</button>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <StageBadge stage={r.stage} />
              <MediaBadges r={r} />
              <ConditionsExpiryBadge r={r} />
              <ConditionsResult r={r} />
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-6 py-4 space-y-4">
            {/* Dane */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <Info label="Referat" value={r.client_name} />
              <Info label="Kontakt" value={r.applicant_contact} />
              <Info label="SUW / źródło" value={r.suw_name} />
              <Info label="Nr warunków" value={r.conditions_number} />
              <Info label="Data warunków" value={r.conditions_date} />
              <Info label="Ważne do" value={r.conditions_valid_until} />
              <Info label="Nr umowy" value={r.contract_number} />
              <Info label="Inwentaryzacja" value={r.contract_number || r.stage === 'completed' ? (r.has_inventory ? 'Jest' : 'Brak') : null} />
            </div>

            {/* Akcje etapu */}
            {canManage && (
              <StageActions
                stage={r.stage}
                busy={busy}
                onAnalyze={() => setStage('analysis')}
                onConditions={() => setDialog('conditions')}
                onToDesign={() => setStage('design')}
                onToAgreement={() => setStage('agreement')}
                onAgreement={() => setDialog('agreement')}
                onAcceptance={() => setDialog('acceptance')}
                onContract={() => setDialog('contract')}
                onEdit={() => onEdit(r)}
                onManualStage={() => setDialog('stage')}
                onCancel={() => {
                  if (window.confirm('Anulować sprawę?')) setStage('cancelled')
                }}
              />
            )}

            {/* Oś czasu */}
            <div>
              <p className="text-sm font-medium text-gray-800 mb-2">Historia sprawy</p>
              <ol className="relative border-l-2 border-gray-100 ml-2 space-y-3">
                {(r.steps || []).slice().reverse().map((ev: TimelineEvent) => (
                  <li key={ev.id} className="ml-4">
                    <div className="absolute -left-[7px] w-3 h-3 rounded-full bg-blue-500 border-2 border-white" />
                    <p className="text-sm text-gray-800">{eventLabel(ev.step_type)}</p>
                    {ev.notes && <p className="text-xs text-gray-500">{ev.notes}</p>}
                    <p className="text-[11px] text-gray-400">
                      {ev.created_at ? new Date(ev.created_at).toLocaleString('pl-PL') : ''}
                      {ev.user_name ? ` • ${ev.user_name}` : ''}
                    </p>
                  </li>
                ))}
                {(!r.steps || r.steps.length === 0) && <li className="ml-4 text-sm text-gray-400">Brak zdarzeń.</li>}
              </ol>
            </div>
          </div>

          {dialog === 'conditions' && <ConditionsDialog r={r} busy={busy} onCancel={() => setDialog(null)} onSave={(b) => post('/conditions', b)} />}
          {dialog === 'agreement' && <AgreementDialog busy={busy} onCancel={() => setDialog(null)} onSave={(b) => post('/agreement', b)} />}
          {dialog === 'acceptance' && <AcceptanceDialog r={r} busy={busy} onCancel={() => setDialog(null)} onSave={(b) => post('/acceptance', b)} />}
          {dialog === 'contract' && <ContractDialog r={r} busy={busy} onCancel={() => setDialog(null)} onSave={(b) => post('/contract', b)} />}
          {dialog === 'stage' && <StageDialog current={r.stage} busy={busy} onCancel={() => setDialog(null)} onSave={(s) => setStage(s)} />}
        </>
      )}
    </Overlay>
  )
}

function Overlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-stretch justify-center z-[9999] sm:items-center sm:p-4" onClick={onClose}>
      <div className="bg-white sm:rounded-xl shadow-2xl w-full sm:max-w-2xl flex flex-col max-h-full sm:max-h-[92vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  )
}

function Info({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex justify-between gap-2 border-b border-gray-50 py-1">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-900 text-right">{value || '—'}</span>
    </div>
  )
}

function Btn({ onClick, disabled, children, kind = 'primary' }: { onClick: () => void; disabled?: boolean; children: React.ReactNode; kind?: 'primary' | 'ghost' | 'danger' }) {
  const cls = kind === 'primary'
    ? 'text-white bg-blue-600 hover:bg-blue-700'
    : kind === 'danger'
      ? 'text-red-700 bg-red-50 hover:bg-red-100'
      : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
  return (
    <button onClick={onClick} disabled={disabled} className={`min-h-[44px] px-3 py-2 text-sm font-medium rounded-lg disabled:opacity-50 ${cls}`}>
      {children}
    </button>
  )
}

function StageActions(props: {
  stage: string; busy: boolean
  onAnalyze: () => void; onConditions: () => void; onToDesign: () => void; onToAgreement: () => void
  onAgreement: () => void; onAcceptance: () => void; onContract: () => void
  onEdit: () => void; onManualStage: () => void; onCancel: () => void
}) {
  const { stage, busy } = props
  return (
    <div className="rounded-xl border border-gray-200 p-3 bg-gray-50/60">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Następny krok</p>
      <div className="flex flex-wrap gap-2">
        {stage === 'application' && <Btn onClick={props.onAnalyze} disabled={busy}>▶ Rozpocznij analizę</Btn>}
        {stage === 'analysis' && <Btn onClick={props.onConditions} disabled={busy}>📝 Wydaj warunki</Btn>}
        {stage === 'conditions' && <Btn onClick={props.onConditions} disabled={busy}>📝 Dokończ decyzję o warunkach</Btn>}
        {stage === 'design' && <Btn onClick={props.onToAgreement} disabled={busy}>📐 Projekt złożony → uzgodnienie</Btn>}
        {stage === 'agreement' && <Btn onClick={props.onAgreement} disabled={busy}>🤝 Uzgodnij projekt</Btn>}
        {stage === 'fieldwork' && <Btn onClick={props.onAcceptance} disabled={busy}>🏁 Odbiór</Btn>}
        {stage === 'acceptance' && <Btn onClick={props.onAcceptance} disabled={busy}>🏁 Dokończ odbiór</Btn>}
        {stage === 'contract' && <Btn onClick={props.onContract} disabled={busy}>📄 Umowa zawarta</Btn>}
        <Btn onClick={props.onEdit} kind="ghost">✏️ Edytuj dane</Btn>
        <Btn onClick={props.onManualStage} kind="ghost">↔ Zmień etap</Btn>
        {!['completed', 'rejected', 'cancelled'].includes(stage) && <Btn onClick={props.onCancel} kind="danger">Anuluj sprawę</Btn>}
      </div>
    </div>
  )
}

function DialogShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10000] p-4" onClick={(e) => e.stopPropagation()}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold text-gray-900 mb-3">{title}</h3>
        {children}
      </div>
    </div>
  )
}

function ConditionsDialog({ r, busy, onCancel, onSave }: { r: Realization; busy: boolean; onCancel: () => void; onSave: (b: any) => void }) {
  const [water, setWater] = useState<string>(r.water_conditions || '')
  const [sewer, setSewer] = useState<string>(r.sewer_conditions || '')
  const [number, setNumber] = useState(r.conditions_number || '')
  const [date, setDate] = useState(r.conditions_date || '')
  const [validUntil, setValidUntil] = useState(r.conditions_valid_until || '')
  const sel = (val: string, set: (v: string) => void) => (
    <div className="flex gap-2">
      {[['issued', 'Wydaj'], ['rejected', 'Odrzuć']].map(([v, l]) => (
        <button key={v} onClick={() => set(val === v ? '' : v)}
          className={`flex-1 min-h-[40px] rounded-lg text-sm font-medium ${val === v ? (v === 'issued' ? 'bg-green-600 text-white' : 'bg-red-600 text-white') : 'bg-gray-100 text-gray-700'}`}>
          {l}
        </button>
      ))}
    </div>
  )
  return (
    <DialogShell title="Decyzja o warunkach">
      <div className="space-y-3">
        {r.connection_water && <div><p className="text-sm font-medium mb-1">💧 Woda</p>{sel(water, setWater)}</div>}
        {r.connection_sewer && <div><p className="text-sm font-medium mb-1">🚿 Kanalizacja</p>{sel(sewer, setSewer)}</div>}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <label className="text-sm">Nr warunków
            <input value={number} onChange={(e) => setNumber(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-2 text-sm" />
          </label>
          <label className="text-sm">Data
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-2 text-sm" />
          </label>
          <label className="text-sm">Ważne do
            <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-2 text-sm" />
          </label>
        </div>
      </div>
      <DialogButtons busy={busy} onCancel={onCancel}
        onSave={() => onSave({ water: water || null, sewer: sewer || null, number, date: date || null, valid_until: validUntil || null })} />
    </DialogShell>
  )
}

function AgreementDialog({ busy, onCancel, onSave }: { busy: boolean; onCancel: () => void; onSave: (b: any) => void }) {
  const [notes, setNotes] = useState('')
  return (
    <DialogShell title="Uzgodnienie projektu">
      <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Uwagi (opcjonalnie)"
        className="w-full rounded-lg border border-gray-300 px-2 py-2 text-sm" rows={3} />
      <div className="mt-3 flex gap-2">
        <button disabled={busy} onClick={() => onSave({ result: 'positive', notes })}
          className="flex-1 min-h-[44px] rounded-lg bg-green-600 text-white text-sm font-medium disabled:opacity-50">✅ Pozytywnie</button>
        <button disabled={busy} onClick={() => onSave({ result: 'rework', notes })}
          className="flex-1 min-h-[44px] rounded-lg bg-amber-500 text-white text-sm font-medium disabled:opacity-50">↩ Do poprawy</button>
      </div>
      <div className="mt-2 text-right">
        <button onClick={onCancel} className="text-sm text-gray-500 hover:text-gray-700">Anuluj</button>
      </div>
    </DialogShell>
  )
}

function AcceptanceDialog({ r, busy, onCancel, onSave }: { r: Realization; busy: boolean; onCancel: () => void; onSave: (b: any) => void }) {
  const [water, setWater] = useState(!!r.water_accepted)
  const [sewer, setSewer] = useState(!!r.sewer_accepted)
  const [notes, setNotes] = useState('')
  return (
    <DialogShell title="Odbiór przyłącza">
      <div className="space-y-2">
        {r.connection_water && (
          <label className="flex items-center gap-3 py-1"><input type="checkbox" checked={water} onChange={(e) => setWater(e.target.checked)} className="h-5 w-5" /><span className="text-sm">💧 Woda — odebrane</span></label>
        )}
        {r.connection_sewer && (
          <label className="flex items-center gap-3 py-1"><input type="checkbox" checked={sewer} onChange={(e) => setSewer(e.target.checked)} className="h-5 w-5" /><span className="text-sm">🚿 Kanalizacja — odebrane</span></label>
        )}
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Uwagi z odbioru (opcjonalnie)"
          className="w-full rounded-lg border border-gray-300 px-2 py-2 text-sm" rows={2} />
        <p className="text-[11px] text-gray-400">Protokół odbioru z podpisami zostanie dodany w kolejnym etapie.</p>
      </div>
      <DialogButtons busy={busy} onCancel={onCancel}
        onSave={() => onSave({ water_accepted: r.connection_water ? water : null, sewer_accepted: r.connection_sewer ? sewer : null, notes })} />
    </DialogShell>
  )
}

function ContractDialog({ r, busy, onCancel, onSave }: { r: Realization; busy: boolean; onCancel: () => void; onSave: (b: any) => void }) {
  const [number, setNumber] = useState(r.contract_number || '')
  const [inventory, setInventory] = useState(!!r.has_inventory)
  return (
    <DialogShell title="Umowa zawarta">
      <div className="space-y-3">
        <label className="block text-sm">Nr umowy
          <input value={number} onChange={(e) => setNumber(e.target.value)} placeholder="np. UM/2026/123"
            className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-2 text-sm" />
        </label>
        <label className="flex items-center gap-3 py-1">
          <input type="checkbox" checked={inventory} onChange={(e) => setInventory(e.target.checked)} className="h-5 w-5" />
          <span className="text-sm">Inwentaryzacja geodezyjna — <strong>{inventory ? 'jest' : 'brak'}</strong></span>
        </label>
        <p className="text-[11px] text-gray-400">Zapisanie zamyka sprawę (etap „Zakończone”) i pokazuje innym urzędnikom, że umowa została zawarta.</p>
      </div>
      <DialogButtons busy={busy} onCancel={onCancel}
        onSave={() => onSave({ contract_number: number || null, has_inventory: inventory })} />
    </DialogShell>
  )
}

function StageDialog({ current, busy, onCancel, onSave }: { current: string; busy: boolean; onCancel: () => void; onSave: (s: string) => void }) {
  const [s, setS] = useState(current)
  const opts: [string, string][] = [
    ['application', 'Wniosek'], ['analysis', 'Analiza'], ['conditions', 'Warunki'], ['design', 'Projekt'],
    ['agreement', 'Uzgodnienie'], ['fieldwork', 'Realizacja'], ['acceptance', 'Odbiór'], ['contract', 'Umowa'],
    ['completed', 'Zakończone'], ['rejected', 'Odrzucone'], ['cancelled', 'Anulowane'],
  ]
  return (
    <DialogShell title="Zmień etap (korekta)">
      <select value={s} onChange={(e) => setS(e.target.value)} className="w-full rounded-lg border border-gray-300 px-2 py-2 text-sm">
        {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <DialogButtons busy={busy} onCancel={onCancel} onSave={() => onSave(s)} />
    </DialogShell>
  )
}

function DialogButtons({ busy, onCancel, onSave }: { busy: boolean; onCancel: () => void; onSave: () => void }) {
  return (
    <div className="mt-4 flex justify-end gap-2">
      <button onClick={onCancel} className="min-h-[44px] px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg">Anuluj</button>
      <button onClick={onSave} disabled={busy} className="min-h-[44px] px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg disabled:opacity-50">
        {busy ? 'Zapisywanie…' : 'Zapisz'}
      </button>
    </div>
  )
}
